import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Chip from '@mui/material/Chip';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputBase from '@mui/material/InputBase';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import AuthContext from '../../authAndConnections/auth';
import AxiosGlobal from '../../authAndConnections/axiosGlobalUrl';
import MisDateField from '../misDateField';
import { DateSystemSelect } from '../docSettings';
import { timeFramePresets, activeTimeFrame } from './timeFrames';

// The filters of the Packing Lists screen, in a drawer on the right (the same place the other sections keep theirs):
//   · the time frame - ready-made frames ("Last 7 days", "This month" ...) and a from / to range in the calendar of
//     the person's choice
//   · the status
//   · the VALUES already saved in the branch's lists - loading officer, customs agent, driver, loading origin, final
//     destination ... - each with the number of lists that hold it. Picking one narrows the list to them; picking
//     several in one group shows the lists that have ANY of them; different groups must ALL hold.
// What is offered always follows the search, the time frame and the other picks, so the numbers are what you will get.

// the order the groups come in (the ones people reach for first on top), and their headings
export const FACET_GROUPS = [
  ['loadingOfficer', 'mis.plFacetLoadingOfficer'],
  ['customsAgent', 'mis.plFacetCustomsAgent'],
  ['driver', 'mis.plFacetDriver'],
  ['origin', 'mis.plFacetOrigin'],
  ['destination', 'mis.plFacetDestination'],
  ['shippingDestination', 'mis.plFacetShippingDestination'],
  ['stone', 'mis.plFacetStone'],
  ['productCode', 'mis.plFacetProductCode'],
  ['sort', 'mis.plFacetSort'],
  ['processing', 'mis.plFacetProcessing'],
  ['trailerPlate', 'mis.plFacetTrailerPlate'],
  ['createdBy', 'mis.plFacetCreatedBy'],
];
const FIRST_OPEN = 5;      // the first groups start open: officer, customs agent, driver, origin, destination (those with values)
const SHOWN = 8;           // options shown before "Show more"

export const EMPTY_FILTERS = { status: '', dateFrom: '', dateTo: '', picks: {} };

// { facet: [{ key, label }] } -> { facet: [key] }, the shape the API takes
export const picksToKeys = (picks) => Object.fromEntries(Object.entries(picks || {}).filter(([, v]) => v && v.length).map(([k, v]) => [k, v.map((p) => p.key)]));
export const countFilters = (f) => (f.status ? 1 : 0) + ((f.dateFrom || f.dateTo) ? 1 : 0) + Object.values(f.picks || {}).reduce((n, v) => n + v.length, 0);

// One group of values: a heading that opens and closes, a find box when there are many, and the checkboxes.
// At MODULE scope on purpose (a component declared inside another is a new type on every render and loses its state).
function FacetGroup({ name, title, options, picked, onToggle, T, defaultOpen }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(defaultOpen || picked.length > 0);
  const [query, setQuery] = useState('');
  const [all, setAll] = useState(false);

  // what is picked stays on top - even when the other filters left it with no lists, so it can still be un-picked
  const rows = useMemo(() => {
    const byKey = new Map(options.map((o) => [o.key, o]));
    const chosen = picked.map((p) => byKey.get(p.key) || { key: p.key, label: p.label, count: 0 });
    const chosenKeys = new Set(picked.map((p) => p.key));
    return [...chosen, ...options.filter((o) => !chosenKeys.has(o.key))];
  }, [options, picked]);
  const pickedKeys = useMemo(() => new Set(picked.map((p) => p.key)), [picked]);
  const q = query.trim().toLowerCase();
  const visible = q ? rows.filter((o) => o.label.toLowerCase().includes(q) || pickedKeys.has(o.key)) : rows;
  const shown = (all || q) ? visible : visible.slice(0, SHOWN);

  return (
    <Box sx={{ borderBottom: `1px solid ${T.BD}` }}>
      <Box role="button" tabIndex={0} aria-expanded={open} onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.key === ' ') { e.preventDefault(); setOpen((v) => !v); } }}
        sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.1, cursor: 'pointer', userSelect: 'none',
          '&:hover': { bgcolor: T.CTRL_BG } }}>
        <Typography sx={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI }}>{title}</Typography>
        {picked.length > 0 && (
          <Chip size="small" label={picked.length} sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
        )}
        <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_TER, fontVariantNumeric: 'tabular-nums' }}>{options.length}</Typography>
        <ExpandMoreIcon sx={{ fontSize: 18, color: T.TEXT_TER, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </Box>
      {open && (
        <Box sx={{ px: 1.5, pb: 1.25 }}>
          {rows.length > SHOWN && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mx: 0.5, mb: 0.75, px: 1, height: 28,
              bgcolor: T.CTRL_BG, border: `1px solid ${T.BD}`, borderRadius: '8px' }}>
              <SearchIcon sx={{ fontSize: 14, color: T.TEXT_TER }} />
              <InputBase value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('mis.plFilterFind')}
                inputProps={{ 'aria-label': `${title} - ${t('mis.plFilterFind')}` }}
                sx={{ flex: 1, fontSize: '0.75rem', color: T.TEXT_PRI, '& input::placeholder': { color: T.TEXT_TER, opacity: 1 } }} />
            </Box>
          )}
          {shown.map((o) => (
            <FormControlLabel key={o.key} sx={{ m: 0, width: '100%', alignItems: 'center', '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 } }}
              control={<Checkbox size="small" checked={pickedKeys.has(o.key)} onChange={() => onToggle(name, o)} sx={{ p: 0.5 }} />}
              label={(
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography title={o.label} noWrap sx={{ flex: 1, minWidth: 0, fontSize: '0.78rem', color: o.count ? T.TEXT_PRI : T.TEXT_TER }}>{o.label}</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: T.TEXT_SEC, fontVariantNumeric: 'tabular-nums' }}>{o.count}</Typography>
                </Box>
              )} />
          ))}
          {q && shown.length === 0 && (
            <Typography sx={{ px: 1, py: 0.5, fontSize: '0.72rem', color: T.TEXT_TER }}>{t('mis.plFilterNoMatch')}</Typography>
          )}
          {!q && visible.length > SHOWN && (
            <Button size="small" onClick={() => setAll((v) => !v)}
              sx={{ mt: 0.25, ml: 0.5, fontSize: '0.7rem', textTransform: 'none', color: T.TEXT_SEC }}>
              {all ? t('mis.plFilterLess') : t('mis.plFilterMore', { count: visible.length - SHOWN })}
            </Button>
          )}
        </Box>
      )}
    </Box>
  );
}

export default function PackingListFilterDrawer({
  open, onClose, T, branchId, search, type, filters, onFiltersChange, calendar, onCalendarChange,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const authCtx = useContext(AuthContext);
  const axiosGlobal = useContext(AxiosGlobal);
  const api = axiosGlobal.defaultTargetApi;
  const [data, setData] = useState({ facets: {}, total: null });
  const [loading, setLoading] = useState(false);
  const requestRef = useRef(0);

  const keysJson = useMemo(() => JSON.stringify(picksToKeys(filters.picks)), [filters.picks]);
  const presets = useMemo(() => timeFramePresets(calendar), [calendar, open]);   // eslint-disable-line react-hooks/exhaustive-deps
  const frame = activeTimeFrame(presets, filters.dateFrom, filters.dateTo);

  // What can be picked, and how many lists each would leave, asked of the server with the SAME search, time frame, status
  // and picks as the list - so the numbers are the list's own. Only while the drawer is open; the answer for an older
  // request never replaces a newer one.
  useEffect(() => {
    if (!open || !branchId) return undefined;
    const id = requestRef.current + 1;
    requestRef.current = id;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await authCtx.jwtInst({
          method: 'get', url: `${api}/mis/packing-lists/facets`,
          params: {
            branchId,
            ...(search && search.trim() ? { search: search.trim() } : {}),
            ...(type && type !== 'all' ? { type } : {}),
            ...(filters.status ? { status: filters.status } : {}),
            ...(filters.dateFrom ? { dateFrom: filters.dateFrom } : {}),
            ...(filters.dateTo ? { dateTo: filters.dateTo } : {}),
            ...(keysJson !== '{}' ? { filters: keysJson } : {}),
          },
        });
        if (requestRef.current === id) setData({ facets: res.data.facets || {}, total: res.data.total });
      } catch (_) {
        /* what is on screen stays; the list reports its own failures */
      } finally {
        if (requestRef.current === id) setLoading(false);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [open, branchId, search, type, filters.status, filters.dateFrom, filters.dateTo, keysJson, authCtx, api]);

  const set = (patch) => onFiltersChange({ ...filters, ...patch });
  const toggle = (name, option) => {
    const current = filters.picks[name] || [];
    const has = current.some((p) => p.key === option.key);
    const next = has ? current.filter((p) => p.key !== option.key) : [...current, { key: option.key, label: option.label }];
    const picks = { ...filters.picks };
    if (next.length) picks[name] = next; else delete picks[name];
    set({ picks });
  };

  const groups = FACET_GROUPS.filter(([name]) => (data.facets[name] && data.facets[name].length) || (filters.picks[name] || []).length);
  // a group of one person is no filter (a user who only sees their own lists): offered only when there is a choice
  const visibleGroups = groups.filter(([name]) => name !== 'createdBy' || (data.facets.createdBy || []).length > 1 || (filters.picks.createdBy || []).length);
  const activeCount = countFilters(filters);

  const chipSx = (active) => ({
    height: 26, fontSize: '0.72rem', fontWeight: active ? 700 : 500, borderColor: T.BD2,
    ...(active ? { bgcolor: theme.palette.text.primary, color: theme.palette.background.paper, '&:hover': { bgcolor: theme.palette.text.primary } } : {}),
  });

  return (
    <Drawer anchor="right" open={open} onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100vw', sm: 360 }, bgcolor: T.PANEL_BG, borderLeft: `1px solid ${T.BD}`, display: 'flex', flexDirection: 'column' } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 2, py: 1.5, borderBottom: `1px solid ${T.BD}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, color: T.TEXT_PRI, flexGrow: 1 }}>{t('common.filters')}</Typography>
        {loading && <Typography sx={{ fontSize: '0.66rem', color: T.TEXT_TER, mr: 1 }}>{t('mis.plFilterUpdating')}</Typography>}
        <IconButton size="small" onClick={onClose} aria-label={t('common.close')} sx={{ color: T.TEXT_TER }}>
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* ── Time frame ── */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.BD}` }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI, mb: 1 }}>{t('mis.plFilterTimeFrame')}</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
            <Chip clickable size="small" variant="outlined" label={t('mis.plPresetAny')} sx={chipSx(frame === 'any')}
              onClick={() => set({ dateFrom: '', dateTo: '' })} />
            {presets.map((p) => (
              <Chip key={p.key} clickable size="small" variant="outlined" label={t(p.labelKey)} sx={chipSx(frame === p.key)}
                onClick={() => set({ dateFrom: p.from, dateTo: p.to })} />
            ))}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <DateSystemSelect value={calendar} onChange={onCalendarChange}
              sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: T.BD } }} />
            <MisDateField label={t('mis.fromDateLabel')} system={calendar} clearable value={filters.dateFrom}
              onChange={(iso) => set({ dateFrom: iso })} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: T.BD } }} />
            <MisDateField label={t('mis.toDateLabel')} system={calendar} clearable value={filters.dateTo}
              onChange={(iso) => set({ dateTo: iso })} sx={{ '& .MuiOutlinedInput-notchedOutline': { borderColor: T.BD } }} />
          </Box>
        </Box>

        {/* ── Status ── */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: `1px solid ${T.BD}` }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI, mb: 1 }}>{t('common.status')}</Typography>
          <ToggleButtonGroup size="small" exclusive value={filters.status || 'all'}
            onChange={(_, v) => v && set({ status: v === 'all' ? '' : v })}>
            {[['all', t('common.all')], ['draft', t('mis.plStatusDraft')], ['final', t('mis.plStatusFinal')]].map(([v, l]) => (
              <ToggleButton key={v} value={v} sx={{ px: 1.5, py: 0.25, fontSize: '0.7rem', textTransform: 'none', borderColor: T.BD, color: T.TEXT_SEC }}>{l}</ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        {/* ── The values already saved in the lists ── */}
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: T.TEXT_PRI }}>{t('mis.plFilterBy')}</Typography>
          <Typography sx={{ fontSize: '0.68rem', color: T.TEXT_TER, mt: 0.25 }}>{t('mis.plFilterHint')}</Typography>
        </Box>
        {visibleGroups.map(([name, titleKey], i) => (
          <FacetGroup key={name} name={name} title={t(titleKey)} options={data.facets[name] || []}
            picked={filters.picks[name] || []} onToggle={toggle} T={T} defaultOpen={i < FIRST_OPEN} />
        ))}
        {visibleGroups.length === 0 && !loading && (
          <Typography sx={{ px: 2, py: 1.5, fontSize: '0.75rem', color: T.TEXT_TER }}>{t('mis.plFilterNothingSaved')}</Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, borderTop: `1px solid ${T.BD}`, flexShrink: 0,
        pb: 'max(10px, env(safe-area-inset-bottom))' }}>
        <Button size="small" disabled={activeCount === 0} onClick={() => onFiltersChange(EMPTY_FILTERS)}
          sx={{ fontSize: '0.75rem', textTransform: 'none', color: T.TEXT_SEC }}>{t('mis.clearAll')}</Button>
        <Button variant="contained" size="small" onClick={onClose} sx={{ ml: 'auto', fontSize: '0.75rem', textTransform: 'none', fontWeight: 700, borderRadius: '8px' }}>
          {t('mis.plFilterShowResults', { count: data.total == null ? '…' : data.total })}
        </Button>
      </Box>
    </Drawer>
  );
}
